import type { RenderAudio } from '../audio/render';
import { debugCounters } from '../lib/debug';
import { Observable } from '../lib/store';
import type { Project } from '../domain/schemas';
import { RECORDER_TYPES, opusConfig, videoEncoderConfig } from './capabilities';
import { MAX_ENCODE_QUEUE, abortable, browserCodecApis, drain, encodeOpus, yieldToEventLoop, type CodecApis, type EncodedAudio } from './encode';
import {
  AUDIO_BITRATE,
  AUDIO_SAMPLE_RATE,
  MAX_VIDEO_BYTES,
  VIDEO_RESOLUTIONS,
  checkRange,
  estimateExport,
  framePosition,
  frameTimestampUs,
  isKeyFrame,
  KEYFRAME_INTERVAL_SEC,
  MAX_OUTPUT_BYTES,
  MAX_REMUX_BYTES,
  formatMb,
  outputTooLarge,
  sanitizeFileName,
  videoBitrate,
  type ExportMethod,
  type VideoCodec,
  type VideoSettings,
} from './frames';
import { SessionFrameRenderer, type FrameSource } from './frameRenderer';
import { renderSessionAudio, type SessionAudio } from './sessionAudio';
import type { AudioFeatures } from '../visualizer/analysis';
import { takeSnapshot, type ExportSnapshot } from './snapshot';
import { WebmMuxer, parseWebm } from './webm';

/**
 * Video export orchestration (Phase 4B). One export at a time, in phases:
 *
 *   preparing-assets → preparing-audio → rendering (frames → encoder) → finalizing (flush, mux)
 *
 * Paths:
 *  - `offline` (frame-accurate): every frame i is rendered at i/fps and encoded with
 *    timestamp i/fps; audio is rendered offline and encoded to Opus; both are muxed into
 *    WebM. Speed does not affect the result.
 *  - `realtime` (fallback): the same frames are drawn on animation frames, timed by an
 *    AudioContext playing the offline-rendered audio into a MediaRecorder. Not
 *    deterministic: drawn and dropped frames are counted and reported.
 *
 * Cleanup on success, failure and cancel: the export renderer (Pixi app, WebGL context,
 * textures, canvas), encoders, audio contexts, recorders, streams and listeners. A
 * cancelled or failed export never offers a file.
 */

export type ExportPhase = 'preparing-assets' | 'preparing-audio' | 'rendering' | 'finalizing';

export const PHASE_LABELS: Record<ExportPhase, string> = {
  'preparing-assets': 'Preparing assets',
  'preparing-audio': 'Preparing audio',
  rendering: 'Rendering frames',
  finalizing: 'Encoding and muxing',
};

const PHASE_WEIGHT: Record<ExportPhase, [number, number]> = {
  'preparing-assets': [0, 0.05],
  'preparing-audio': [0.05, 0.2],
  rendering: [0.2, 0.95],
  finalizing: [0.95, 1],
};

export interface ExportReport {
  method: ExportMethod;
  codec: VideoCodec;
  mimeType: string;
  width: number;
  height: number;
  fps: number;
  frames: number;
  /** Requested range (seconds on the session timeline). */
  range: { start: number; end: number };
  /** Video length written to the file (frames / fps). */
  durationSec: number;
  bytes: number;
  audio: { included: boolean; packets: number; peak: number; normalizedDb: number; notes: number; clips: number };
  /** Phase 4C: visible visualizer layers, whether measured analysis was used, and measured layers drawn from events instead. */
  visualizer: { layers: number; measured: boolean; fallbackLayers: number };
  timings: {
    prepareMs: number;
    audioMs: number;
    /** The whole frame loop; within it: drawing, reading the canvas into VideoFrames, and waiting for the encoder. */
    framesMs: number;
    renderMs: number;
    captureMs: number;
    waitMs: number;
    finalizeMs: number;
    totalMs: number;
  };
  /** Real-time recording only: frames actually drawn, frames missed, and the longest gap. */
  /**
   * Real-time recording only: frames drawn, frames actually in the file (counted when
   * re-muxing), frames missing from the file, the longest gap between drawn frames, and the
   * audio start offset relative to the first video frame as recorded.
   */
  realtime?: { drawnFrames: number; recordedFrames: number | null; droppedFrames: number; maxGapMs: number; remuxed: boolean; keptReason: RemuxKeptReason | null; audioOffsetMs: number | null };
}

export interface VideoExportState {
  status: 'idle' | 'running' | 'done' | 'cancelled' | 'error';
  method?: ExportMethod;
  phase?: ExportPhase;
  /** Overall progress 0–1. */
  progress: number;
  framesDone: number;
  framesTotal: number;
  startedAt?: number;
  /** Real-time recording: seconds recorded and remaining. */
  elapsedSec?: number;
  remainingSec?: number;
  url?: string;
  fileName?: string;
  report?: ExportReport;
  error?: string;
}

export interface ExporterDeps {
  loadImage?: (assetId: string) => Promise<ImageBitmap>;
  audio?: RenderAudio;
  download?: (url: string, fileName: string) => void;
  /** Test seams. */
  codecs?: () => CodecApis;
  createRenderer?: (snapshot: ExportSnapshot, width: number, height: number, features: AudioFeatures | null) => Promise<FrameSource>;
  /**
   * Phase 4C: measured audio features for the snapshot's visualizer layers (null when not
   * needed or unavailable: those layers then use their event-driven fallback). Shared with
   * the preview's analysis cache; cancellable.
   */
  analysis?: (snapshot: ExportSnapshot, signal: AbortSignal) => Promise<AudioFeatures | null>;
  renderAudio?: (snapshot: ExportSnapshot, start: number, samples: number, signal: AbortSignal, onProgress: (f: number) => void) => Promise<Pick<SessionAudio, 'buffer' | 'peak' | 'normalizedDb' | 'notes' | 'clips' | 'renderMs'>>;
  now?: () => number;
  /** Hard limit on actual encoded output (default MAX_OUTPUT_BYTES; read at each export). */
  maxOutputBytes?: () => number;
  /** Real-time recordings larger than this are kept as recorded instead of re-muxed (default MAX_REMUX_BYTES). */
  maxRemuxBytes?: () => number;
}

export class VideoExportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VideoExportError';
  }
}

/** The encoded output grew past the hard size limit (no file is offered). */
export class OutputLimitError extends VideoExportError {
  constructor(bytes: number, limit: number, progress: string) {
    super(
      `The video grew too large: about ${formatMb(bytes)} after ${progress}, past the ${formatMb(limit)} output limit. ` +
        'No file was saved. Choose a lower resolution or quality, or a shorter range.',
    );
    this.name = 'OutputLimitError';
  }
}

const isAbort = (e: unknown) => e instanceof DOMException && e.name === 'AbortError';

export class VideoExporter {
  readonly state = new Observable<VideoExportState>({ status: 'idle', progress: 0, framesDone: 0, framesTotal: 0 });
  private controller: AbortController | null = null;
  private running: Promise<unknown> | null = null;
  private readonly deps: ExporterDeps;
  /** Exports started and fully cleaned up (leak diagnostics). */
  readonly counters = { started: 0, cleaned: 0, renderersCreated: 0, renderersDestroyed: 0, encodersOpen: 0 };
  private lastVisualizer: ExportReport['visualizer'] = { layers: 0, measured: false, fallbackLayers: 0 };

  constructor(deps: ExporterDeps = {}) {
    this.deps = deps;
  }

  get busy(): boolean {
    return this.state.get().status === 'running';
  }

  /** Runs one export; resolves with its report, rejects on failure or cancellation (AbortError). */
  async export(project: Project, sessionId: string, settings: VideoSettings): Promise<ExportReport> {
    if (this.busy) throw new VideoExportError('A video export is already running.');
    this.revoke();
    const controller = new AbortController();
    this.controller = controller;
    const now = this.deps.now ?? (() => performance.now());
    this.counters.started++;
    this.state.set({ status: 'running', method: settings.method, phase: 'preparing-assets', progress: 0, framesDone: 0, framesTotal: 0, startedAt: Date.now() });
    const run = (async () => {
      const t0 = now();
      const snapshot = takeSnapshot(project, sessionId);
      const range = checkRange(settings.range, snapshot.timing.durationSec, settings.fps);
      if (!range.ok) throw new VideoExportError(range.error);
      const span = { start: range.start, end: range.end };
      const estimate = estimateExport(settings, range.end - range.start);
      if (estimate.bytes > MAX_VIDEO_BYTES) {
        throw new VideoExportError(`This export would be about ${Math.round(estimate.bytes / 1024 / 1024)} MB; the limit is ${MAX_VIDEO_BYTES / 1024 / 1024} MB. Choose a lower resolution or quality, or a shorter range.`);
      }
      return settings.method === 'offline' ? this.runOffline(snapshot, settings, span, estimate.frames, controller.signal, t0) : this.runRealtime(snapshot, settings, span, estimate.frames, controller.signal, t0);
    })();
    this.running = run;
    try {
      const { blob, report } = await run;
      if (controller.signal.aborted) throw controller.signal.reason;
      const url = URL.createObjectURL(blob);
      const fileName = sanitizeFileName(settings.fileName || sessionName(project, sessionId));
      this.state.set({ ...this.state.get(), status: 'done', progress: 1, url, fileName, report, phase: undefined });
      debugCounters.videoExport = () => ({ ...this.state.get(), counters: { ...this.counters } });
      this.deps.download?.(url, fileName);
      return report;
    } catch (error) {
      if (controller.signal.aborted || isAbort(error)) {
        this.state.set({ ...this.state.get(), status: 'cancelled', phase: undefined, error: undefined });
        throw controller.signal.reason ?? error;
      }
      const message = error instanceof Error ? error.message : String(error);
      this.state.set({ ...this.state.get(), status: 'error', phase: undefined, error: message });
      throw error;
    } finally {
      this.counters.cleaned++;
      if (this.controller === controller) this.controller = null;
      this.running = null;
      debugCounters.videoExport = () => ({ ...this.state.get(), counters: { ...this.counters } });
    }
  }

  /** Cancels the running export (prompt: checked between frames and inside every wait). */
  cancel(): void {
    this.controller?.abort(new DOMException('The export was cancelled.', 'AbortError'));
  }

  /** Waits for a running export to settle (after cancel). */
  async settled(): Promise<void> {
    await this.running?.catch(() => undefined);
  }

  downloadAgain(): void {
    const { url, fileName } = this.state.get();
    if (url && fileName) this.deps.download?.(url, fileName);
  }

  /** Forgets the finished result (revokes its object URL). */
  reset(): void {
    if (this.busy) return;
    this.revoke();
    this.state.set({ status: 'idle', progress: 0, framesDone: 0, framesTotal: 0 });
  }

  dispose(): void {
    this.cancel();
    this.revoke();
  }

  private revoke(): void {
    const { url } = this.state.get();
    if (url) URL.revokeObjectURL(url);
    if (url) this.state.update({ url: undefined });
  }

  private progress(phase: ExportPhase, fraction: number, extra: Partial<VideoExportState> = {}): void {
    // A late callback (an encoder or render settling after a cancel) never revives a finished export.
    if (this.state.get().status !== 'running') return;
    const [a, b] = PHASE_WEIGHT[phase];
    this.state.update({ phase, progress: a + (b - a) * Math.min(1, Math.max(0, fraction)), ...extra });
  }

  private async renderer(snapshot: ExportSnapshot, settings: VideoSettings, signal: AbortSignal): Promise<FrameSource> {
    const { width, height } = VIDEO_RESOLUTIONS[settings.resolution];
    // Measured visualizer features first (the analysis cache usually already has them).
    const features = snapshot.session.visualizer.layers.length && this.deps.analysis ? await this.deps.analysis(snapshot, signal) : null;
    this.lastVisualizer = { layers: snapshot.session.visualizer.layers.filter((l) => l.visible).length, measured: !!features, fallbackLayers: features ? 0 : snapshot.session.visualizer.layers.filter((l) => l.visible && l.response.source === 'measured').length };
    signal.throwIfAborted();
    const create = this.deps.createRenderer ?? ((s, w, h, f) => SessionFrameRenderer.create(s, w, h, { loadImage: this.deps.loadImage, features: f }));
    const r = await create(snapshot, width, height, features);
    this.counters.renderersCreated++;
    const destroy = r.destroy.bind(r);
    let done = false;
    r.destroy = () => {
      if (done) return;
      done = true;
      destroy();
      this.counters.renderersDestroyed++;
    };
    return r;
  }

  /** Creates the renderer; one that finishes after a cancel is destroyed, never leaked. */
  private async cancellableRenderer(snapshot: ExportSnapshot, settings: VideoSettings, signal: AbortSignal): Promise<FrameSource> {
    const pending = this.renderer(snapshot, settings, signal);
    try {
      return await Promise.race([pending, abortable(signal)]);
    } catch (error) {
      pending.then(
        (r) => r.destroy(),
        () => undefined,
      );
      throw error;
    }
  }

  private async audio(snapshot: ExportSnapshot, start: number, samples: number, signal: AbortSignal) {
    const render =
      this.deps.renderAudio ??
      ((s, from, n, sig, onProgress) => renderSessionAudio(s.project, s.session, from, n, AUDIO_SAMPLE_RATE, { audio: this.deps.audio, signal: sig, onProgress }));
    return render(snapshot, start, samples, signal, (f) => this.progress('preparing-audio', f * 0.6));
  }

  // ---------------------------------------------------------------- offline

  private async runOffline(snapshot: ExportSnapshot, settings: VideoSettings, range: { start: number; end: number }, frames: number, signal: AbortSignal, t0: number) {
    const now = this.deps.now ?? (() => performance.now());
    const apis = (this.deps.codecs ?? browserCodecApis)();
    const { width, height } = VIDEO_RESOLUTIONS[settings.resolution];
    const fps = settings.fps;
    const videoSec = frames / fps;
    this.state.update({ framesTotal: frames });
    let renderer: FrameSource | null = null;
    let encoder: VideoEncoder | null = null;
    try {
      this.progress('preparing-assets', 0);
      renderer = await this.cancellableRenderer(snapshot, settings, signal);
      const t1 = now();

      let audio: EncodedAudio | null = null;
      let audioInfo: ExportReport['audio'] = { included: false, packets: 0, peak: 0, normalizedDb: 0, notes: 0, clips: 0 };
      if (settings.includeAudio) {
        this.progress('preparing-audio', 0);
        const samples = Math.round(videoSec * AUDIO_SAMPLE_RATE);
        const rendered = await this.audio(snapshot, range.start, samples, signal);
        this.progress('preparing-audio', 0.6);
        audio = await encodeOpus(rendered.buffer, apis, opusConfig(), signal, (f) => this.progress('preparing-audio', 0.6 + 0.4 * f));
        audioInfo = { included: true, packets: audio.packets.length, peak: rendered.peak, normalizedDb: rendered.normalizedDb, notes: rendered.notes, clips: rendered.clips };
      }
      const t2 = now();

      // Actual output, not the estimate: audio bytes are final; video bytes are checked as each
      // chunk arrives (hard limit, plus an early stop when the growth projects far past it).
      const limit = this.deps.maxOutputBytes?.() ?? MAX_OUTPUT_BYTES;
      const audioBytes = audio?.bytes ?? 0;
      if (audioBytes > limit) throw new OutputLimitError(audioBytes, limit, 'encoding the audio');
      let videoBytes = 0;
      let videoChunks = 0;
      let sizeFailure: OutputLimitError | null = null;
      const minFrames = Math.max(Math.round(fps * KEYFRAME_INTERVAL_SEC), Math.ceil(frames * 0.1));
      const muxer = new WebmMuxer({ codec: settings.codec, width, height, fps }, audio ? { opusHead: audio.opusHead, sampleRate: AUDIO_SAMPLE_RATE, channels: 2 } : null);
      if (audio) {
        for (const p of audio.packets) muxer.addAudio(p.data, p.timestampUs);
        audio.packets.length = 0;
      }
      let failure: Error | null = null;
      let muxError: Error | null = null;
      encoder = new apis.VideoEncoder({
        output: (chunk) => {
          if (sizeFailure) return; // stopped: nothing more is kept
          videoBytes += chunk.byteLength;
          videoChunks++;
          if (outputTooLarge(audioBytes, videoBytes, videoChunks, frames, limit, minFrames)) {
            sizeFailure = new OutputLimitError(audioBytes + videoBytes, limit, `${videoChunks} of ${frames} frames`);
            return;
          }
          try {
            const data = new Uint8Array(chunk.byteLength);
            chunk.copyTo(data);
            muxer.addVideo(data, chunk.timestamp, chunk.type === 'key');
          } catch (e) {
            muxError ??= e instanceof Error ? e : new Error(String(e));
          }
        },
        error: (e) => (failure ??= new VideoExportError(`Video encoding failed: ${e.message}`)),
      });
      this.counters.encodersOpen++;
      encoder.configure(videoEncoderConfig(settings.codec, settings.resolution, fps, videoBitrate(settings.resolution, fps, settings.quality)));
      const failed = () => failure ?? muxError ?? sizeFailure;
      const frameUs = Math.round(1e6 / fps);
      let renderMs = 0;
      let captureMs = 0;
      let waitMs = 0;
      this.progress('rendering', 0);
      for (let i = 0; i < frames; i++) {
        const w0 = now();
        await drain(encoder, MAX_ENCODE_QUEUE, signal, failed);
        const r0 = now();
        waitMs += r0 - w0;
        renderer.renderFrame(framePosition(range.start, i, fps, snapshot.timing.loop));
        const c0 = now();
        renderMs += c0 - r0;
        const frame = new apis.VideoFrame(renderer.canvas as CanvasImageSource, { timestamp: frameTimestampUs(i, fps), duration: frameUs });
        captureMs += now() - c0;
        try {
          encoder.encode(frame, { keyFrame: isKeyFrame(i, fps) });
        } finally {
          frame.close();
        }
        if (i % 4 === 3 || i === frames - 1) this.progress('rendering', (i + 1) / frames, { framesDone: i + 1 });
        await yieldToEventLoop();
        signal.throwIfAborted();
      }
      const t3 = now();
      this.progress('finalizing', 0);
      await Promise.race([encoder.flush(), abortable(signal)]);
      if (failed()) throw failed();
      this.progress('finalizing', 0.6);
      const blob = muxer.finish(videoSec * 1000);
      // The container's own bytes are small, but the file offered must respect the limit too.
      if (blob.size > limit) throw new OutputLimitError(blob.size, limit, 'muxing');
      const t4 = now();
      const report: ExportReport = {
        method: 'offline',
        codec: settings.codec,
        mimeType: blob.type,
        width,
        height,
        fps,
        frames,
        range,
        durationSec: videoSec,
        bytes: blob.size,
        audio: audioInfo,
        visualizer: this.lastVisualizer,
        timings: { prepareMs: Math.round(t1 - t0), audioMs: Math.round(t2 - t1), framesMs: Math.round(t3 - t2), renderMs: Math.round(renderMs), captureMs: Math.round(captureMs), waitMs: Math.round(waitMs), finalizeMs: Math.round(t4 - t3), totalMs: Math.round(t4 - t0) },
      };
      if (muxer.stats.videoBlocks !== frames) throw new VideoExportError(`The encoder returned ${muxer.stats.videoBlocks} of ${frames} frames.`);
      return { blob, report };
    } finally {
      if (encoder) {
        if (encoder.state !== 'closed') encoder.close();
        this.counters.encodersOpen--;
      }
      renderer?.destroy();
    }
  }

  // ---------------------------------------------------------------- real time

  private async runRealtime(snapshot: ExportSnapshot, settings: VideoSettings, range: { start: number; end: number }, frames: number, signal: AbortSignal, t0: number) {
    const now = this.deps.now ?? (() => performance.now());
    const { width, height } = VIDEO_RESOLUTIONS[settings.resolution];
    const fps = settings.fps;
    const videoSec = frames / fps;
    this.state.update({ framesTotal: frames });
    let renderer: FrameSource | null = null;
    let ctx: AudioContext | null = null;
    let recorder: MediaRecorder | null = null;
    let stream: MediaStream | null = null;
    let surface: HTMLCanvasElement | null = null;
    let raf = 0;
    try {
      this.progress('preparing-assets', 0);
      renderer = await this.cancellableRenderer(snapshot, settings, signal);
      const t1 = now();
      // The context is created before any await on audio so it stays tied to the Export click.
      ctx = new AudioContext({ sampleRate: AUDIO_SAMPLE_RATE });
      let audioInfo: ExportReport['audio'] = { included: false, packets: 0, peak: 0, normalizedDb: 0, notes: 0, clips: 0 };
      let buffer: AudioBuffer | null = null;
      if (settings.includeAudio) {
        this.progress('preparing-audio', 0);
        const rendered = await this.audio(snapshot, range.start, Math.round(videoSec * AUDIO_SAMPLE_RATE), signal);
        buffer = rendered.buffer;
        audioInfo = { included: true, packets: 0, peak: rendered.peak, normalizedDb: rendered.normalizedDb, notes: rendered.notes, clips: rendered.clips };
      }
      await Promise.race([ctx.resume(), abortable(signal)]);
      if (ctx.state !== 'running') throw new VideoExportError('The browser did not allow audio for the recording. Click Export again.');
      const t2 = now();

      // A detached WebGL canvas does not deliver captured frames reliably until it is
      // presented; each rendered frame is copied into a 2D canvas, which is captured instead.
      surface = document.createElement('canvas');
      surface.width = width;
      surface.height = height;
      const g = surface.getContext('2d');
      if (!g) throw new VideoExportError('A 2D canvas is not available for recording.');
      const src = renderer;
      const draw = (index: number) => {
        src.renderFrame(framePosition(range.start, index, fps, snapshot.timing.loop));
        g.drawImage(src.canvas as CanvasImageSource, 0, 0, width, height);
      };
      const videoStream = surface.captureStream(0);
      const track = videoStream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
      if (!track) throw new VideoExportError('The canvas could not be captured.');
      const tracks: MediaStreamTrack[] = [track];
      let source: AudioBufferSourceNode | null = null;
      if (buffer) {
        const dest = ctx.createMediaStreamDestination();
        source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(dest);
        // The recorder starts only once every track delivers data: a silent source keeps the
        // audio track flowing before the session audio starts.
        const silence = ctx.createConstantSource();
        silence.offset.value = 0;
        silence.connect(dest);
        silence.start();
        tracks.push(...dest.stream.getAudioTracks());
      }
      stream = new MediaStream(tracks);
      // Without audio the type names only the video codec (no Opus track is recorded).
      const mimeType = buffer ? RECORDER_TYPES[settings.codec] : `video/webm;codecs=${settings.codec}`;
      const chunks: Blob[] = [];
      let startAt = Infinity;
      const rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: videoBitrate(settings.resolution, fps, settings.quality), audioBitsPerSecond: AUDIO_BITRATE });
      recorder = rec;
      // Actual recorded bytes are checked as they arrive (once a second), like the offline path.
      const limit = this.deps.maxOutputBytes?.() ?? MAX_OUTPUT_BYTES;
      let recordedBytes = 0;
      let sizeFailure: OutputLimitError | null = null;
      rec.ondataavailable = (e) => {
        if (sizeFailure || !e.data.size) return;
        recordedBytes += e.data.size;
        const elapsed = Math.max(0, ctx!.currentTime - startAt);
        if (outputTooLarge(0, recordedBytes, elapsed, videoSec, limit, KEYFRAME_INTERVAL_SEC)) {
          sizeFailure = new OutputLimitError(recordedBytes, limit, `${Math.min(elapsed, videoSec).toFixed(1)} s of ${videoSec.toFixed(1)} s`);
          chunks.length = 0; // drop what was recorded: no partial file
          return;
        }
        chunks.push(e.data);
      };
      const stopped = new Promise<void>((resolve, reject) => {
        rec.onstop = () => resolve();
        rec.onerror = (e) => reject(new VideoExportError(`Recording failed: ${(e as unknown as { error?: Error }).error?.message ?? 'unknown error'}`));
      });
      stopped.catch(() => undefined);

      // The recorder drops what arrives before it runs, and only starts once frames arrive:
      // frame 0 is drawn and requested on every animation frame until it has started; the timeline
      // (frame 0, then the audio) begins at that instant.
      let recording = false;
      rec.onstart = () => (recording = true);
      // (startAt is set when the timeline starts; data before that counts as 0 s elapsed.)
      rec.start(1000);
      await new Promise<void>((resolve, reject) => {
        const warm = () => {
          if (signal.aborted) return reject(signal.reason);
          if (rec.state === 'inactive') return reject(new VideoExportError('The recorder stopped before it started.'));
          // An unchanged canvas yields no captured frame: redraw before each request.
          draw(0);
          track.requestFrame();
          if (recording) resolve();
          else raf = requestAnimationFrame(warm);
        };
        warm();
      });
      startAt = ctx.currentTime;
      source?.start(startAt);
      let drawn = 1;
      let last = 0;
      let lastAt = now();
      let maxGapMs = 0;
      let renderMs = 0;
      this.progress('rendering', 0, { framesDone: 1, elapsedSec: 0, remainingSec: videoSec });
      await new Promise<void>((resolve, reject) => {
        const abort = () => reject(signal.reason);
        signal.addEventListener('abort', abort, { once: true });
        const tick = () => {
          if (signal.aborted) return;
          if (sizeFailure) {
            signal.removeEventListener('abort', abort);
            reject(sizeFailure);
            return;
          }
          const t = ctx!.currentTime - startAt;
          const index = Math.min(frames - 1, Math.floor(t * fps + 1e-6));
          if (index > last) {
            const r0 = now();
            draw(index);
            track.requestFrame();
            renderMs += now() - r0;
            const at = now();
            maxGapMs = Math.max(maxGapMs, at - lastAt);
            lastAt = at;
            drawn++;
            last = index;
          }
          this.progress('rendering', Math.min(1, t / videoSec), { framesDone: drawn, elapsedSec: Math.min(t, videoSec), remainingSec: Math.max(0, videoSec - t) });
          // A short tail lets the last frames and audio reach the recorder (trimmed when re-muxing).
          if (t >= videoSec + TAIL_SEC) {
            signal.removeEventListener('abort', abort);
            resolve();
          } else raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      });
      const t3 = now();
      this.progress('finalizing', 0);
      rec.stop();
      await Promise.race([stopped, abortable(signal)]);
      if (sizeFailure) throw sizeFailure; // the last data arrives with stop()
      const raw = new Blob(chunks, { type: mimeType });
      chunks.length = 0;
      if (raw.size > limit) throw new OutputLimitError(raw.size, limit, 'recording');
      this.progress('finalizing', 0.5);
      const { blob, remuxed, videoFrames, audioOffsetMs, keptReason } = await remuxRecording(raw, settings.codec, width, height, fps, videoSec, {
        signal,
        maxBytes: this.deps.maxRemuxBytes?.() ?? MAX_REMUX_BYTES,
      });
      const t4 = now();
      const report: ExportReport = {
        method: 'realtime',
        codec: settings.codec,
        mimeType: blob.type,
        width,
        height,
        fps,
        frames,
        range,
        durationSec: videoSec,
        bytes: blob.size,
        audio: audioInfo,
        visualizer: this.lastVisualizer,
        timings: { prepareMs: Math.round(t1 - t0), audioMs: Math.round(t2 - t1), framesMs: Math.round(t3 - t2), renderMs: Math.round(renderMs), captureMs: 0, waitMs: 0, finalizeMs: Math.round(t4 - t3), totalMs: Math.round(t4 - t0) },
        realtime: { drawnFrames: drawn, recordedFrames: videoFrames, droppedFrames: Math.max(0, frames - (videoFrames ?? drawn)), maxGapMs: Math.round(maxGapMs), remuxed, keptReason, audioOffsetMs },
      };
      return { blob, report };
    } finally {
      cancelAnimationFrame(raf);
      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null;
        recorder.stop();
      }
      stream?.getTracks().forEach((t) => t.stop());
      if (ctx) await ctx.close().catch(() => undefined);
      if (surface) surface.width = surface.height = 0;
      renderer?.destroy();
    }
  }
}

/** Seconds recorded past the end so nothing in flight is lost (trimmed from the file). */
const TAIL_SEC = 0.3;

const sessionName = (project: Project, sessionId: string) => project.avSessions.find((s) => s.id === sessionId)?.name ?? project.name;

/** Why a real-time recording was kept exactly as recorded instead of re-muxed. */
export type RemuxKeptReason = 'too-large' | 'unparseable' | 'no-video';

export interface RemuxResult {
  blob: Blob;
  remuxed: boolean;
  /** Set when the recording was kept as recorded (still valid WebM, without duration and cues). */
  keptReason: RemuxKeptReason | null;
  videoFrames: number | null;
  audioOffsetMs: number | null;
}

/**
 * MediaRecorder writes WebM without a duration or cues (players then show an unknown length
 * and cannot seek). The recording is re-muxed with the same muxer as offline export.
 *
 * Memory: re-muxing reads the recording into one ArrayBuffer and parses it in place (packets
 * are views, not copies); the new file is built cluster by cluster into Blobs. At the end
 * the recording Blob, its bytes and the new file coexist — about 3× the recording. Above
 * `maxBytes` (default MAX_REMUX_BYTES, 128 MB → at most ~384 MB) the recording is kept as
 * recorded instead. A recording that cannot be parsed is kept too. Cancellation is checked
 * after reading and before writing; a cancelled re-mux rejects with the AbortError.
 */
export async function remuxRecording(
  raw: Blob,
  codec: VideoCodec,
  width: number,
  height: number,
  fps: number,
  durationSec: number,
  options: { signal?: AbortSignal; maxBytes?: number } = {},
): Promise<RemuxResult> {
  const kept = (keptReason: RemuxKeptReason): RemuxResult => ({ blob: raw, remuxed: false, keptReason, videoFrames: null, audioOffsetMs: null });
  if (raw.size > (options.maxBytes ?? MAX_REMUX_BYTES)) return kept('too-large');
  options.signal?.throwIfAborted();
  const bytes = new Uint8Array(await raw.arrayBuffer());
  options.signal?.throwIfAborted();
  try {
    const parsed = parseWebm(bytes);
    const video = parsed.tracks.find((t) => t.type === 1);
    const audio = parsed.tracks.find((t) => t.type === 2 && t.codecId === 'A_OPUS');
    if (parsed.truncated) return kept('unparseable');
    if (!video) return kept('no-video');
    const scale = parsed.timecodeScale / 1e6; // to ms
    const vBlocks = parsed.blocks.filter((b) => b.track === video.number);
    const aBlocks = audio ? parsed.blocks.filter((b) => b.track === audio.number) : [];
    if (vBlocks.length === 0 || !vBlocks[0]!.key) return kept('no-video');
    const origin = Math.min(vBlocks[0]!.time, aBlocks[0]?.time ?? Infinity);
    const endMs = durationSec * 1000;
    const inside = (b: { time: number }) => (b.time - origin) * scale < endMs;
    const muxer = new WebmMuxer(
      { codec, width: video.width || width, height: video.height || height, fps },
      audio ? { opusHead: audio.codecPrivate ?? new Uint8Array(0), sampleRate: audio.sampleRate || AUDIO_SAMPLE_RATE, channels: audio.channels || 2 } : null,
    );
    for (const b of aBlocks.filter(inside).sort((x, y) => x.time - y.time)) muxer.addAudio(b.data, Math.round((b.time - origin) * scale * 1000));
    let lastMs = -1;
    const shown = vBlocks.filter(inside);
    for (const b of shown) {
      const ms = Math.max(lastMs, (b.time - origin) * scale);
      muxer.addVideo(b.data, Math.round(ms * 1000), b.key);
      lastMs = ms;
    }
    options.signal?.throwIfAborted();
    const audioOffsetMs = aBlocks.length ? Math.round((aBlocks[0]!.time - vBlocks[0]!.time) * scale) : null;
    return { blob: muxer.finish(endMs), remuxed: true, keptReason: null, videoFrames: shown.length, audioOffsetMs };
  } catch {
    if (options.signal?.aborted) throw options.signal.reason;
    return kept('unparseable');
  }
}

